<script lang="ts">
	import type { OperationModel } from '../models/api/OperationModel';
	import type { OperationSummaryModel } from '../models/api/OperationSummaryModel';
	import {
		getAmountNeededSummary,
		getOperationModelAmountNeeded,
		getPercentageCollectedAfterAmountMin,
		getPercentageCollectedBeforeAmountMin,
		getPercentageCollectedOutsideBeforeAmountMin,
		hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks,
		isCollectAlmostEnd,
		isCollectRunning,
		isMinAmountReachedFn,
		isOperationSummaryModel
	} from '@app/utils/operations.js';
	import { tooltipAction } from '@wiseed/wikit';
	import { t } from 'svelte-i18n';
	import { formatAmountWithCurrency } from '@wiseed/wikit';
	import type { TooltipProps } from '@wiseed/wikit';

	export let operation: OperationModel | OperationSummaryModel;

	let percentageCollectedBeforeAmountMin: number;
	let percentageCollectedOutsideBeforeAmountMin: number;
	let percentageCollectedAfterAmountMin: number;
	let percentageCollectedTooltip: TooltipProps;
	let amountRemainingToNeededMin: number | undefined;
	let amountRemainingToNeeded: number | undefined;
	let isMinAmountReached: boolean;
	let isProgressReady: boolean;

	const operationAmount = isOperationSummaryModel(operation)
		? getAmountNeededSummary(operation)
		: getOperationModelAmountNeeded(operation);
	if (hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(operation)) {
		percentageCollectedOutsideBeforeAmountMin =
			getPercentageCollectedOutsideBeforeAmountMin(operation);
	}

	percentageCollectedBeforeAmountMin = getPercentageCollectedBeforeAmountMin(operation);
	percentageCollectedAfterAmountMin = getPercentageCollectedAfterAmountMin(operation);
	percentageCollectedAfterAmountMin =
		percentageCollectedAfterAmountMin > 95 ? 100 : percentageCollectedAfterAmountMin;
	percentageCollectedTooltip = {
		title:
			percentageCollectedAfterAmountMin >= 95
				? $t('collectProgress.amountNeededAlmostReached')
				: $t('collectProgress.amountCollected', {
						values: {
							n: hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(operation) ? 1 : 2,
							amount: formatAmountWithCurrency(operation.amountCollected)
						}
					}),
		placement: 'top'
	};
	amountRemainingToNeededMin = operation.amountNeededMin - operation.amountCollected;
	amountRemainingToNeeded = operationAmount - operation.amountCollected;

	if (hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(operation)) {
		amountRemainingToNeededMin =
			operation.amountNeededMin +
			operation.amountReachedExcludingWiseed -
			(operation.amountCollected + operation.amountReachedExcludingWiseed);
		amountRemainingToNeeded = amountRemainingToNeeded - operation.amountReachedExcludingWiseed;
	}

	isMinAmountReached = isMinAmountReachedFn(operation);

	setTimeout(() => {
		isProgressReady = true;
	}, 100);
</script>

<div class="relative flex rounded-full h-3 w-full bg-grey-light text-success">
	<div class="relative overflow-hidden w-4/5">
		{#if isCollectRunning(operation) || isCollectAlmostEnd(operation)}
			<div
				class="absolute h-full"
				style="width:{100 -
					(percentageCollectedOutsideBeforeAmountMin
						? percentageCollectedOutsideBeforeAmountMin + percentageCollectedBeforeAmountMin
						: percentageCollectedBeforeAmountMin)}%;left:{percentageCollectedOutsideBeforeAmountMin
					? percentageCollectedOutsideBeforeAmountMin + percentageCollectedBeforeAmountMin
					: percentageCollectedBeforeAmountMin}%"
				use:tooltipAction={{
					title: $t('collectProgress.amountRemainingToNeededMin', {
						values: {
							n: hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(operation) ? 1 : 2,
							amount: formatAmountWithCurrency(amountRemainingToNeededMin)
						}
					}),
					placement: 'top'
				}}
			/>
			{#if percentageCollectedOutsideBeforeAmountMin}
				<div
					class="absolute rounded-l-lg {isMinAmountReached
						? 'bg-success'
						: 'bg-grey-darker'} {isProgressReady
						? 'border-r border-grey-light'
						: ''} h-full flex items-center"
					style="width: {isProgressReady
						? percentageCollectedOutsideBeforeAmountMin
						: 0}%;transition: width .7s"
					use:tooltipAction={{
						title: $t('collectProgress.amountReachedExcludingWiseed', {
							values: {
								amount: formatAmountWithCurrency(operation.amountReachedExcludingWiseed)
							}
						})
					}}
				/>
			{/if}
			<div
				class="absolute {!percentageCollectedOutsideBeforeAmountMin
					? percentageCollectedBeforeAmountMin === 100
						? 'rounded-l-lg'
						: 'rounded-full'
					: ''} {isMinAmountReached ? 'bg-success' : 'bg-grey-darker'}  h-full flex items-center"
				style="width: {isProgressReady
					? percentageCollectedBeforeAmountMin
					: 0}%;left:{percentageCollectedOutsideBeforeAmountMin
					? percentageCollectedOutsideBeforeAmountMin
					: 0}%;transition: width .7s {percentageCollectedOutsideBeforeAmountMin
					? '.7s'
					: ''} ease-out"
				use:tooltipAction={percentageCollectedTooltip}
			/>
		{/if}
	</div>
	<div class="relative w-1/5 overflow-hidden">
		{#if isCollectRunning(operation) || isCollectAlmostEnd(operation)}
			{#if isMinAmountReached}
				<div
					class="absolute rounded-r-lg bg-success h-full flex items-center"
					style="left:0%; width: {isProgressReady
						? percentageCollectedAfterAmountMin
						: 0}%;transition: width .7s {percentageCollectedOutsideBeforeAmountMin
						? '1.4s'
						: '.7s'} ease-out"
					use:tooltipAction={percentageCollectedTooltip}
				/>
			{/if}
			<div
				class="absolute h-full"
				style="width:{100 -
					percentageCollectedAfterAmountMin}%;left:{percentageCollectedAfterAmountMin}%"
				use:tooltipAction={{
					title: $t('collectProgress.amountRemainingToNeeded', {
						values: {
							n: hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(operation) ? 1 : 2,
							amount: formatAmountWithCurrency(amountRemainingToNeeded)
						}
					}),
					placement: 'top'
				}}
			/>
		{/if}
	</div>
	<div
		class="cursor absolute {isMinAmountReached
			? 'border-grey-light'
			: 'border-success '} {isMinAmountReached ? 'border-r' : 'border-r-2'} h-full text-secondary"
		style="left: 80%"
		use:tooltipAction={{
			title: $t('collectProgress.amountMinNeeded')
		}}
	/>
</div>

<style lang="postcss">
	.cursor:before {
		content: '';
		display: block;
		position: absolute;
		height: 0;
		width: 0;
		border-right: solid 5px transparent;
		border-left: solid 5px transparent;
		border-top: solid 5px theme('colors.success.DEFAULT');
		top: -5px;
		left: -4px;
	}
</style>
